import type { OwnershipGraph } from './entity'
import { ultimateOwnership } from './entity'
import { fxStanding } from './fx'
import type { Minor, Quantity } from './money'
import {
  applyBasisPoints,
  assertMinor,
  convertMinor,
  isZeroQuantity,
  subMinor,
  sumMinor,
  valueOf,
} from './money'
import type { ExtractionTax, ExtractionTaxFn, ExtractionTaxInput } from './tax'
import { settingsExtractionTax } from './tax'

export type { ExtractionTax, ExtractionTaxFn, ExtractionTaxInput }

export interface RollupAccount {
  id: number
  entityKey: string
  name: string
  source: string
  kind: 'cash' | 'brokerage' | 'wallet' | 'private'
  currency: string
  liquid: boolean
}

/** Accounts and instruments both mark what has no market to sell into this way. */
export const PRIVATE_KIND = 'private'

/**
 * Liquidity is recorded on the account and implied by its kind, and the two can
 * be set against each other; an unsellable holding wins, so that what can be
 * spent never turns on which of the two a data entry got right.
 */
export function isLiquidAccount(account: Pick<RollupAccount, 'kind' | 'liquid'>): boolean {
  return account.liquid && account.kind !== PRIVATE_KIND
}

/**
 * Sellability belongs to the holding: a private instrument booked into a liquid
 * brokerage account is the same unsellable thing as one in a private account,
 * and reading the account alone counted it as money that could be spent. A kind
 * nobody supplied leaves the account to answer, which is what it did before.
 */
export function isSellableHolding(
  account: Pick<RollupAccount, 'kind' | 'liquid'>,
  instrumentKind: string | undefined,
): boolean {
  return isLiquidAccount(account) && isSellableInstrument(instrumentKind)
}

/**
 * The instrument's half of that rule, for a caller holding a verdict the rollup
 * has already reached about the account: restating the exception beside it is
 * how a row came to contradict the total that had applied it.
 */
export function isSellableInstrument(instrumentKind: string | undefined): boolean {
  return instrumentKind !== PRIVATE_KIND
}

/**
 * A company's gains on unlisted shares are exempt under aktieavancebeskatnings-
 * loven — as datterselskabsaktier from 10% ownership, as skattefri portefølje-
 * aktier below it — and a bank balance is not a security at all. Anything else,
 * including a kind nobody recognises, is charged: understating the bill would
 * put money into `spendable` that cannot be taken out.
 *
 * This is one jurisdiction's rule and nothing states that it is the household's,
 * so what it spares is said out loud rather than left to be discovered as a
 * shortfall. Nothing here reads a setting: a household cannot change the rule,
 * only find out which one it got.
 */
const GAINS_EXEMPT_KIND_LIST = [PRIVATE_KIND, 'cash'] as const

type GainsExemptKind = (typeof GAINS_EXEMPT_KIND_LIST)[number]

const GAINS_EXEMPT_KINDS: ReadonlySet<string> = new Set(GAINS_EXEMPT_KIND_LIST)

export function isCorporateGainsCharged(instrumentKind: string | undefined): boolean {
  return instrumentKind === undefined || !GAINS_EXEMPT_KINDS.has(instrumentKind)
}

/**
 * What a spared kind is called to a reader who has never seen the union it comes
 * from. Typed against the list rather than beside it, so a kind added to the
 * exemption without a name here does not compile — the alternative is a figure
 * that goes on explaining itself in terms of the rule it used to apply.
 */
const GAINS_EXEMPT_KIND_NAMES: Readonly<Record<GainsExemptKind, string>> = {
  [PRIVATE_KIND]: 'unlisted holdings',
  cash: 'cash',
}

/** The spared kinds as prose, for a sentence that has to name them all. */
export const GAINS_EXEMPT_KINDS_PHRASE = joinWithAnd(
  GAINS_EXEMPT_KIND_LIST.map((kind) => GAINS_EXEMPT_KIND_NAMES[kind]),
)

/**
 * The assumption `spendable` rests on, in the words a household reads.
 *
 * It is written where the rule is, not where it is displayed, because a sentence
 * kept beside the set it describes moves when the set does and one kept on a page
 * does not. The consequence is stated rather than implied: a reader who learns
 * only that something was exempted cannot tell whether that made the figure
 * generous or mean.
 */
export const GAINS_EXEMPTION_ASSUMPTION =
  `Gains are charged on everything but ${GAINS_EXEMPT_KINDS_PHRASE}, which Denmark exempts under ` +
  'aktieavancebeskatningsloven. A company taxed elsewhere owes more than this estimate.'

function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

export interface RollupPosition {
  accountId: number
  instrumentId: number
  quantity: Quantity
  confirmed: boolean
  costBasisMinor?: Minor | null
  /**
   * What currency the basis was recorded in, which is the source's and not the
   * household's: an account's for one broker, a position's for another. Absent
   * is "nobody wrote it down", which is not the same claim as the base currency
   * and must never be read as one.
   */
  costBasisCurrency?: string | null
}

/**
 * Stands in for the currency of a basis that was stored before any source
 * recorded one, so a pair nobody can name still reads as a pair that is missing.
 */
export const UNTAGGED_CURRENCY = '?'

export interface InstrumentPrice {
  priceMinor: Minor
  currency: string
}

export interface FxConversion {
  rate: Quantity
  /** The day the rate was published, earlier than the date it is used on where it was carried. */
  observedOn: string
}

export interface RollupInput {
  date: string
  baseCurrency: string
  /** The person the headline figures are computed for. */
  subjectKey: string
  graph: OwnershipGraph
  accounts: readonly RollupAccount[]
  positions: readonly RollupPosition[]
  priceOf(instrumentId: number, date: string): InstrumentPrice | undefined
  /**
   * The rate taking one unit of `from` to `to`, with the day it was published,
   * or undefined where the pair has none usable on the date. The publication
   * day comes with it because a figure converted at a rate carried from an
   * earlier day is a figure the feed did not confirm, and a total cannot say so
   * unless the conversion does.
   */
  fxRate(from: string, to: string, date: string): FxConversion | undefined
  /**
   * `instrument.kind` for a held instrument. Liquidity and the corporate gains
   * charge are both properties of the holding, so without this they fall back to
   * the account, which cannot tell an unsellable share from a tradable one.
   */
  instrumentKindOf?(instrumentId: number): string | undefined
  /** Accounts whose value should be left out entirely (the private-holdings switch). */
  excludeAccountIds?: readonly number[]
  /** Defaults to the tax model reading `settings`; pass one only to override it. */
  extractionTax?: ExtractionTaxFn
  settings?: Readonly<Record<string, unknown>>
}

export interface AccountValue {
  accountId: number
  entityKey: string
  name: string
  source: string
  liquid: boolean
  /** Full value of the account, before ownership is applied. */
  valueMinor: Minor
  /** The subject's share of it. */
  attributedMinor: Minor
  /** The part of `attributedMinor` that could be sold; a private holding is not in it. */
  liquidMinor: Minor
  /** Part of `attributedMinor` that rests on unconfirmed positions. */
  projectedMinor: Minor
  costBasisMinor: Minor
  shareBp: number
}

export interface EntityValue {
  entityKey: string
  valueMinor: Minor
  attributedMinor: Minor
  costBasisMinor: Minor
}

export interface RollupResult {
  date: string
  baseCurrency: string
  lookThroughMinor: Minor
  spendableMinor: Minor
  liquidMinor: Minor
  taxMinor: Minor
  /** False where a company was left untaxed for want of rates, not because it owes nothing. */
  taxRatesConfigured: boolean
  projectedMinor: Minor
  personalMinor: Minor
  companyMinor: Minor
  /**
   * Whether the household holds anything through an entity that is not the
   * subject. Extraction tax is charged against those entities alone, so where
   * this is false `spendableMinor` is `lookThroughMinor` to the øre — and a
   * reader offered both is being offered one answer twice, under a name that
   * describes somebody else's affairs.
   *
   * It asks whether a second entity holds value, not whether the bill came to
   * anything. A company owing nothing this month is still a company, and a
   * measure that appeared and disappeared with the arithmetic would be worse
   * than one that is consistently there; a company with no rates set owes
   * nothing only because nobody has said yet, which the pages explain in terms
   * of a company and could not explain at all if the measure had gone.
   */
  holdsThroughCompany: boolean
  /**
   * Whether anything held could not be sold without asking someone — an illiquid
   * account, or a holding of a kind no market takes. Where it is false,
   * `liquidMinor` is the same figure as Spendable (and so as Look-through when
   * there is no company): Liquid subtracts only what cannot be sold, and here
   * there is nothing to subtract.
   *
   * Asked of the holdings, account by account, like `holdsThroughCompany`, and
   * not of whether two totals happen to agree on the day: an unsellable holding
   * worth nothing this month is still one the household owns.
   */
  holdsUnsellable: boolean
  byAccount: AccountValue[]
  byEntity: EntityValue[]
  /** Instruments held on the date with no price — their value is missing, not zero. */
  missingPrices: number[]
  missingFx: string[]
  /** Pairs converted at a rate published before the date, and the oldest day one came from. */
  carriedFx: string[]
  carriedFxSince?: string
}

/** What a group of holdings hands the tax seam, already attributed to the subject. */
interface TaxBase {
  valueMinor: Minor
  costBasisMinor: Minor
  taxableGainMinor: Minor
}

interface ValuedAccount {
  account: AccountValue
  everything: TaxBase
  sellable: TaxBase
}

export function rollup(input: RollupInput): RollupResult {
  const excluded = new Set(input.excludeAccountIds ?? [])
  const extractionTax = input.extractionTax ?? settingsExtractionTax
  const settings = input.settings ?? {}
  const kindOf = input.instrumentKindOf ?? (() => undefined)

  const missingPrices = new Set<number>()
  const missingFx = new Set<string>()
  const carriedFx = new Set<string>()
  let carriedFxSince: string | undefined

  const positionsByAccount = new Map<number, RollupPosition[]>()
  for (const p of input.positions) {
    const list = positionsByAccount.get(p.accountId) ?? []
    list.push(p)
    positionsByAccount.set(p.accountId, list)
  }

  const shareCache = new Map<string, number>()
  const shareOfEntity = (entityKey: string): number => {
    const cached = shareCache.get(entityKey)
    if (cached !== undefined) return cached
    const share = ultimateOwnership(input.graph, entityKey).get(input.subjectKey) ?? 0
    shareCache.set(entityKey, share)
    return share
  }

  /**
   * The rate taking `currency` into the base on the rollup's date, noting the
   * pair as missing or as carried on the way so that every figure converted here
   * answers for its rate the same way. Undefined is "no rate to convert at", and
   * what that costs is the caller's to decide.
   */
  const rateInto = (currency: string): Quantity | undefined => {
    if (currency === input.baseCurrency) return '1'

    const found = input.fxRate(currency, input.baseCurrency, input.date)
    if (!found) {
      missingFx.add(`${currency}/${input.baseCurrency}`)
      return undefined
    }
    if (fxStanding(found.observedOn, input.date) !== 'in_force') {
      carriedFx.add(`${currency}/${input.baseCurrency}`)
      if (carriedFxSince === undefined || found.observedOn < carriedFxSince) {
        carriedFxSince = found.observedOn
      }
    }
    return found.rate
  }

  /**
   * Nothing may be subtracted from a base-currency value until it is in the base
   * currency itself. Undefined is a basis that exists but cannot be converted,
   * for want of a recorded currency or of a rate for it; the pair is reported and
   * the holding is charged nothing, because a basis nobody can convert must not
   * read as a gain of the entire holding. Zero is the different claim that the
   * source never reported a basis at all.
   */
  const basisInBase = (pos: RollupPosition): Minor | undefined => {
    if (pos.costBasisMinor == null) return 0
    const amount = assertMinor(pos.costBasisMinor)
    if (amount === 0) return 0

    const currency = pos.costBasisCurrency
    if (currency === undefined || currency === null || currency.length === 0) {
      missingFx.add(`${UNTAGGED_CURRENCY}/${input.baseCurrency}`)
      return undefined
    }

    const rate = rateInto(currency)
    return rate === undefined ? undefined : convertMinor(amount, rate)
  }

  const valued: ValuedAccount[] = []
  let holdsUnsellable = false

  for (const acct of input.accounts) {
    if (excluded.has(acct.id)) continue

    let projectedValueMinor = 0
    const everything = emptyBase()
    const sellable = emptyBase()

    for (const pos of positionsByAccount.get(acct.id) ?? []) {
      const price = input.priceOf(pos.instrumentId, input.date)
      if (!price) {
        missingPrices.add(pos.instrumentId)
        continue
      }

      const rate = rateInto(price.currency)
      if (rate === undefined) continue

      const kind = kindOf(pos.instrumentId)
      const value = valueOf(pos.quantity, price.priceMinor, rate)
      const converted = basisInBase(pos)
      const basis = converted ?? 0
      const gain =
        converted !== undefined && isCorporateGainsCharged(kind) ? subMinor(value, basis) : 0

      addHolding(everything, value, basis, gain)
      if (isSellableHolding(acct, kind)) addHolding(sellable, value, basis, gain)
      else if (!isZeroQuantity(pos.quantity)) holdsUnsellable = true
      if (!pos.confirmed) projectedValueMinor += value
    }

    const shareBp = shareOfEntity(acct.entityKey)
    valued.push({
      account: {
        accountId: acct.id,
        entityKey: acct.entityKey,
        name: acct.name,
        source: acct.source,
        liquid: isLiquidAccount(acct),
        valueMinor: assertMinor(everything.valueMinor),
        attributedMinor: applyBasisPoints(everything.valueMinor, shareBp),
        liquidMinor: applyBasisPoints(sellable.valueMinor, shareBp),
        projectedMinor: applyBasisPoints(projectedValueMinor, shareBp),
        costBasisMinor: applyBasisPoints(everything.costBasisMinor, shareBp),
        shareBp,
      },
      everything: attribute(everything, shareBp),
      sellable: attribute(sellable, shareBp),
    })
  }

  const byAccount = valued.map((v) => v.account)

  const lookThroughMinor = sumMinor(byAccount.map((a) => a.attributedMinor))
  const projectedMinor = sumMinor(byAccount.map((a) => a.projectedMinor))

  const byEntity = summariseEntities(byAccount)

  const personalValued = valued.filter((v) => v.account.entityKey === input.subjectKey)
  const companyValued = valued.filter((v) => v.account.entityKey !== input.subjectKey)

  const personalMinor = sumMinor(personalValued.map((v) => v.everything.valueMinor))
  const companyMinor = sumMinor(companyValued.map((v) => v.everything.valueMinor))

  // Account by account rather than against `companyMinor`: two companies whose
  // values cancel sum to nothing while one of them is still charged, and an
  // entity standing empty is a structure on paper that owes nothing and never
  // will until something is put into it.
  const holdsThroughCompany = companyValued.some((v) => v.everything.valueMinor !== 0)

  let taxMinor = 0
  let liquidTaxMinor = 0
  let taxRatesConfigured = true
  for (const group of groupBy(companyValued, (v) => v.account.entityKey)) {
    const chargeOn = (bases: readonly TaxBase[]): ExtractionTax =>
      extractionTax({
        date: input.date,
        baseCurrency: input.baseCurrency,
        companyKey: group.key,
        companyValueMinor: sumMinor(bases.map((b) => b.valueMinor)),
        companyCostBasisMinor: sumMinor(bases.map((b) => b.costBasisMinor)),
        taxableGainMinor: sumMinor(bases.map((b) => b.taxableGainMinor)),
        settings,
      })

    const charged = chargeOn(group.items.map((v) => v.everything))
    taxMinor += assertMinor(charged.taxMinor)
    if (!charged.ratesConfigured) taxRatesConfigured = false

    // Charging liquid a pro-rata slice of the whole bill would make the
    // private-holdings switch move it, and what can be spent is a property of the
    // holdings, not of a display setting.
    if (group.items.some((v) => v.account.liquid)) {
      liquidTaxMinor += assertMinor(chargeOn(group.items.map((v) => v.sellable)).taxMinor)
    }
  }
  taxMinor = assertMinor(taxMinor)
  liquidTaxMinor = assertMinor(liquidTaxMinor)

  const spendableMinor = subMinor(personalMinor + companyMinor, taxMinor)

  const liquidHoldings = sumMinor(valued.map((v) => v.sellable.valueMinor))
  const liquidMinor = subMinor(liquidHoldings, liquidTaxMinor)

  return {
    date: input.date,
    baseCurrency: input.baseCurrency,
    lookThroughMinor,
    spendableMinor,
    liquidMinor,
    taxMinor,
    taxRatesConfigured,
    projectedMinor,
    personalMinor,
    companyMinor,
    holdsThroughCompany,
    holdsUnsellable,
    byAccount,
    byEntity,
    missingPrices: [...missingPrices].sort((a, b) => a - b),
    missingFx: [...missingFx].sort(),
    carriedFx: [...carriedFx].sort(),
    ...(carriedFxSince === undefined ? {} : { carriedFxSince }),
  }
}

function emptyBase(): TaxBase {
  return { valueMinor: 0, costBasisMinor: 0, taxableGainMinor: 0 }
}

function addHolding(base: TaxBase, valueMinor: Minor, costBasisMinor: Minor, gainMinor: Minor): void {
  base.valueMinor += valueMinor
  base.costBasisMinor += costBasisMinor
  base.taxableGainMinor += gainMinor
}

function attribute(base: TaxBase, shareBp: number): TaxBase {
  return {
    valueMinor: applyBasisPoints(base.valueMinor, shareBp),
    costBasisMinor: applyBasisPoints(base.costBasisMinor, shareBp),
    taxableGainMinor: applyBasisPoints(base.taxableGainMinor, shareBp),
  }
}

function summariseEntities(byAccount: readonly AccountValue[]): EntityValue[] {
  return groupBy(byAccount, (a) => a.entityKey).map((group) => ({
    entityKey: group.key,
    valueMinor: sumMinor(group.items.map((a) => a.valueMinor)),
    attributedMinor: sumMinor(group.items.map((a) => a.attributedMinor)),
    costBasisMinor: sumMinor(group.items.map((a) => a.costBasisMinor)),
  }))
}

function groupBy<T>(items: readonly T[], key: (item: T) => string): { key: string; items: T[] }[] {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const k = key(item)
    const list = groups.get(k) ?? []
    list.push(item)
    groups.set(k, list)
  }
  return [...groups].map(([k, list]) => ({ key: k, items: list }))
}
